import React, { useEffect } from 'react';

interface TermsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAccept: () => void;
}

export default function TermsModal({ isOpen, onClose, onAccept }: TermsModalProps) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose?.();
    }
    if (isOpen) document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="tc-title"
      onClick={onClose}
    >
      <div 
        className="w-full max-w-2xl mx-4 bg-white rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 flex-shrink-0">
          <div className="flex items-start justify-between">
            <h2 id="tc-title" className="text-lg font-semibold">Terms & Conditions — Standby Travel</h2>
            <button
              onClick={onClose}
              aria-label="Close terms"
              className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="px-6 pb-6 overflow-y-auto flex-1">
          <div className="text-sm text-gray-700 space-y-4">
            <p className="text-xs text-gray-500">Last Updated: January 2025</p>
            
            <p>
              Welcome to Standby Travel. By using our mobile or web application, you agree to the Terms & Conditions below. Please read them carefully.
            </p>

            <div>
              <h3 className="font-semibold text-base mb-2">1. Eligibility</h3>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>You must be an airline employee or a verified non-rev-eligible user.</li>
                <li>Valid proof (airline ID badge or uniform photo) or official airline email verification is required.</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-base mb-2">2. Account Registration</h3>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>You may sign up using Google, Apple ID, or Email.</li>
                <li>All information provided must be accurate.</li>
                <li>Accounts must be verified through OTP or email link.</li>
                <li>You are responsible for maintaining the confidentiality of your login credentials.</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-base mb-2">3. App Usage Rules</h3>
              <p className="mb-2">You agree NOT to:</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Provide false or misleading load information.</li>
                <li>Access or answer flight loads for airlines you are not associated with.</li>
                <li>Misuse credits, attempt fraud, or bypass security.</li>
              </ul>
              <p className="mt-2">Violation may result in account suspension or permanent blocking.</p>
            </div>

            <div>
              <h3 className="font-semibold text-base mb-2">4. Credits</h3>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Credits are required to post load requests and are earned by answering requests.</li>
                <li>Credit purchases are non-refundable except for system errors.</li>
                <li>If no response is received within 48 hours, credits used for that request will be automatically refunded.</li>
                <li>Credits have no cash value and cannot be converted to money.</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-base mb-2">5. Load Requests & Responses</h3>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Users may post flight load requests using valid flight details.</li>
                <li>Requests cannot be submitted for departed flights.</li>
                <li>Responses submitted by other users are final and visible to the requestor.</li>
                <li>Inaccurate responses may lead to warnings or account restrictions.</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-base mb-2">6. Notifications</h3>
              <p className="mb-2">Users may receive:</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Email alerts</li>
                <li>Push notifications</li>
                <li>In-app updates</li>
              </ul>
              <p className="mt-2">for account activity, request updates, and purchases.</p>
            </div>

            <div>
              <h3 className="font-semibold text-base mb-2">7. Payments</h3>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>All credit purchases use secure payment gateways (e.g., Stripe).</li>
                <li>Standby Travel is not liable for third-party payment failures.</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-base mb-2">8. User Blocking</h3>
              <p className="mb-2">Admins may block accounts for:</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Providing inaccurate or fraudulent load information</li>
                <li>Violating app rules</li>
                <li>Abusive or suspicious activity</li>
              </ul>
              <p className="mt-2">Blocked users cannot access the platform.</p>
            </div>

            <div>
              <h3 className="font-semibold text-base mb-2">9. Data Privacy</h3>
              <p>
                Your data is processed securely and used only for verification, communication, and app functionality. Refer to our Privacy Policy for full details.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-base mb-2">10. Limitation of Liability</h3>
              <p className="mb-2">Standby Travel is not responsible for:</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Inaccurate load data submitted by users</li>
                <li>Airline changes (delays, cancellations, gate changes)</li>
                <li>Loss of credits due to user actions</li>
                <li>Payment gateway issues or network failures</li>
              </ul>
              <p className="mt-2">Use the app at your own discretion.</p>
            </div>

            <div>
              <h3 className="font-semibold text-base mb-2">11. Account Deletion</h3>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Users may delete their accounts anytime.</li>
                <li>All associated data and unused credits will be permanently removed.</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-base mb-2">12. Changes to Terms</h3>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>We may update these Terms occasionally.</li>
                <li>Continued use of the app implies acceptance of updated Terms.</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-base mb-2">13. Contact</h3>
              <p>For support, email us or use the Contact Us form in the app.</p>
            </div>
          </div>
        </div>

        <div className="p-6 border-t flex-shrink-0 bg-gray-50">
          <button
            onClick={onAccept}
            className="w-full px-4 py-3 rounded-md bg-[#FFD700] hover:bg-[#FFC700] text-gray-900 font-semibold text-sm transition-colors"
          >
            I Understand & Accept
          </button>
        </div>
      </div>
    </div>
  );
}
